import _ from 'lodash';
import React, {FC, RefObject} from 'react';
import {connect} from 'react-redux';

import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
  wrapInShallowSelector,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {getCardNoteDisplayDate} from '../../../helpers/models/cardNoteHelpers';
import {ResolveDispatchProps} from '../../../helpers/reduxHelpers';
import {CardModel, CardTypesEnum} from '../../../models/api/cardModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {createCardNoteAsyncAction} from '../../../store/data/cardNotes/cardNotesActions';
import {getCardNotes} from '../../../store/data/dataSelectors';
import {getUserNamespace} from '../../../store/data/teammates/teammatesSelectors';
import {createCardAndUpdateContactDetailsPanelAction} from '../../../store/panels/contacts/contactDetailsPanelActions';
import {makeGetContactDetailsPanel} from '../../../store/panels/contacts/contactDetailsPanelSelectors';
import {ContactDetailsPanelId} from '../../../store/panels/panelStateBase';
import {useContactDetailsContext} from '../contactDetailsContext';
import {ComposerNotePartial} from './contactNoteComposer';
import {ContactNotes, ContactNotesHandle} from './contactNotes';

/*
 * Props.
 */

interface ContactNotesContainerProps {
  panelId: ContactDetailsPanelId;
  card: CardModel | undefined;
  contact?: VisualContactModel;
  notesRef?: RefObject<ContactNotesHandle>;
}

interface ContactNotesContainerComponentProps
  extends ContactNotesContainerProps,
    ResolveStateProps<typeof mapStateToProps>,
    ResolveDispatchProps<typeof mapDispatchToProps> {}

/*
 * Selectors.
 */

function makeGetNoteIds(getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>) {
  return createSelector([getContactDetailsPanel], (panel) => panel?.noteIds ?? []);
}

function makeGetNoteLoadingStatus(getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>) {
  return createSelector([getContactDetailsPanel], (panel) => panel?.notesStatus);
}

function makeGetNotes(getNoteIds: ReturnType<typeof makeGetNoteIds>) {
  return createSelector([getCardNotes, getNoteIds], (notes, noteIds) =>
    _(noteIds)
      .map((noteId) => notes[noteId])
      .compact()
      .sort((a, b) => getCardNoteDisplayDate(b) - getCardNoteDisplayDate(a))
      .value(),
  );
}

/*
 * Component.
 */

const ContactNotesContainerComponent: FC<ContactNotesContainerComponentProps> = (props) => {
  const {
    panelId,
    noteLoadingStatus,
    card,
    notes,
    userNamespace,
    contact,
    notesRef,
    panel,
    createCardNoteAsync,
    createCardAndUpdateContactDetailsPanel,
  } = props;
  const {conversationNamespace} = useContactDetailsContext();

  const createNewCardAsync = async () => {
    if (!conversationNamespace || !contact) {
      return undefined;
    }
    const createCard = await createCardAndUpdateContactDetailsPanel(
      panelId,
      {
        namespace: conversationNamespace,
        name: contact.name || contact.contact.handle,
        contacts: [contact.contact],
        type: CardTypesEnum.AUTO,
      },
      {shouldHideLoadingState: true},
    );
    return createCard;
  };

  return (
    <ContactNotes
      ref={notesRef}
      noteLoadingStatus={noteLoadingStatus}
      card={card}
      notes={notes}
      userNamespace={userNamespace}
      panel={panel}
      createCardNoteAsync={createCardNoteAsync}
      createNewCardAsync={createNewCardAsync}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactNotesContainerProps>()(() => {
  const getContactDetailsPanel = makeGetContactDetailsPanel();
  const getNoteIds = makeGetNoteIds(getContactDetailsPanel);

  return {
    notes: makeGetNotes(getNoteIds),
    noteLoadingStatus: makeGetNoteLoadingStatus(getContactDetailsPanel),
    panel: getContactDetailsPanel,
    userNamespace: wrapInShallowSelector(getUserNamespace),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<ContactNotesContainerProps>()((props) => ({
  createCardNoteAsync: (cardId: number, newNote: ComposerNotePartial) =>
    createCardNoteAsyncAction(cardId, newNote, props.panelId),
  createCardAndUpdateContactDetailsPanel: createCardAndUpdateContactDetailsPanelAction,
}));

export const ContactNotesContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactNotesContainerComponent);
